import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../utils/api';
import { Home, Calendar, FileText, Users, LogOut, Menu, X, Image, UserCheck, ClipboardList, BarChart3, Scale, Settings, User } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = getBackendUrl();

const AdminLayout = () => {
  const { isAuthenticated, logout, loading } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setLoadingUser(false);
        return;
      }
      
      try {
        const response = await fetch(`${API}/admin/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUserInfo(data);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      } finally {
        setLoadingUser(false);
      }
    };
    
    if (isAuthenticated) {
      fetchUserInfo();
    } else {
      setLoadingUser(false);
    }
  }, [isAuthenticated]);

  if (loading || loadingUser) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // All menu items with permission requirements
  const allMenuItems = [
    { path: '/admin/dashboard', icon: Home, label: 'Dashboard', permission: 'dashboard' },
    { path: '/admin/events', icon: Calendar, label: 'Events & Bookings', permission: 'events' },
    { path: '/admin/check-in', icon: UserCheck, label: 'Check-In', permission: 'check-in' },
    { path: '/admin/gallery', icon: Image, label: 'Gallery', permission: 'gallery' },
    { path: '/admin/disclaimers', icon: FileText, label: 'Disclaimers', permission: 'disclaimers' },
    { path: '/admin/legal-pages', icon: Scale, label: 'Legal Pages', permission: 'disclaimers' },
    { path: '/admin/email-templates', icon: FileText, label: 'Email Templates', permission: 'settings' },
    { path: '/admin/bulk-email', icon: Users, label: 'Bulk Email', permission: 'events' },
    { path: '/admin/statistics', icon: BarChart3, label: 'Statistics', permission: 'analytics' },
    { path: '/admin/users', icon: Users, label: 'Admin Users', permission: 'users' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', permission: 'analytics' },
    { path: '/admin/audit-logs', icon: ClipboardList, label: 'Audit Logs', permission: 'audit-logs' },
  ];
  
  // Filter menu items based on user permissions
  const userPermissions = userInfo?.permissions || [];
  const menuItems = allMenuItems.filter(item => userPermissions.includes(item.permission));

  // Get current path and check access
  const currentPath = location.pathname;
  
  // Settings page and My Account are always accessible
  const isSettingsPage = currentPath === '/admin/settings';
  const isMyAccountPage = currentPath === '/admin/my-account';
  const hasAccessToCurrentPage = isSettingsPage || isMyAccountPage || menuItems.some(item => currentPath.startsWith(item.path));
  
  // If user doesn't have access to current page, redirect to their first available page
  if (userInfo && !hasAccessToCurrentPage && menuItems.length > 0) {
    return <Navigate to={menuItems[0].path} replace />;
  }

  const handleNavClick = (path) => {
    setSidebarOpen(false);
    window.location.href = path;
  };

  return (
    <div className="min-h-screen bg-[#1a1f16] overflow-x-hidden">
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#1a1f16] border-b border-[#3d4a35]">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold text-[#c5bfa9] tracking-wide">FALLEN DRAGON</h1>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-[#8b9a7e] hover:text-[#c5bfa9]"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 bottom-0 z-50 w-64 bg-[#1a1f16] text-white flex flex-col
        transform transition-transform duration-300 ease-in-out
        md:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 border-b border-[#3d4a35]">
          <h2 className="text-xl font-bold text-[#c5bfa9] tracking-wide">FALLEN DRAGON</h2>
          <p className="text-sm text-[#6b7a5e] mt-1">Admin Panel</p>
          {userInfo && (
            <div className="mt-3 pt-3 border-t border-[#3d4a35]">
              <p className="text-sm text-[#8b9a7e]">{userInfo.username}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                userInfo.role === 'superadmin' 
                  ? 'bg-purple-900/50 text-purple-300' 
                  : userInfo.role === 'marshal'
                  ? 'bg-blue-900/50 text-blue-300'
                  : 'bg-green-900/50 text-green-300'
              }`}>
                {userInfo.role === 'superadmin' ? 'Super Admin' : userInfo.role === 'marshal' ? 'Marshal' : 'Admin'}
              </span>
            </div>
          )}
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${
                  isActive 
                    ? 'bg-[#3d4a35] text-[#c5bfa9]' 
                    : 'text-[#8b9a7e] hover:bg-[#232921] hover:text-[#c5bfa9]'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-[#3d4a35] space-y-1">
          {/* My Account - always accessible */}
          <button
            onClick={() => handleNavClick('/admin/my-account')}
            className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-left ${
              location.pathname === '/admin/my-account'
                ? 'bg-[#3d4a35] text-[#c5bfa9]' 
                : 'text-[#8b9a7e] hover:bg-[#232921] hover:text-[#c5bfa9]'
            }`}
          >
            <User size={20} />
            <span>My Account</span>
          </button>
          
          {/* Settings - only for admin/superadmin */}
          {(userInfo?.role === 'admin' || userInfo?.role === 'superadmin') && (
            <button
              onClick={() => handleNavClick('/admin/settings')}
              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-left ${
                location.pathname === '/admin/settings'
                  ? 'bg-[#3d4a35] text-[#c5bfa9]' 
                  : 'text-[#8b9a7e] hover:bg-[#232921] hover:text-[#c5bfa9]'
              }`}
            >
              <Settings size={20} />
              <span>Settings</span>
            </button>
          )}
          
          <Button 
            onClick={logout} 
            variant="ghost"
            className="w-full flex items-center gap-3 text-[#8b9a7e] hover:text-[#c5bfa9] hover:bg-[#232921] justify-start"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </Button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-14 md:pt-0 bg-[#1a1f16] overflow-x-hidden">
        <div className="p-4 md:p-8 max-w-full overflow-x-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
